import type {
  GlobalAssistantCurrentPageContext,
  GlobalAssistantDiscoveryKind,
  GlobalAssistantEntityType,
  GlobalAssistantSuggestMatch,
} from './global-ai-assistant-api';
import {
  isScreenerV2DashboardPath,
  isTalentDiscoveryPublicPath,
} from './global-ai-assistant-route-filter-kind';
import { globalAssistantPathsMatch } from './global-assistant-route-filter-path';

export type LastAppliedFilterContext = {
  prompt: string;
  targetPath: string;
  surfaceKind: 'discovery' | 'dashboard';
  discoveryKind?: GlobalAssistantDiscoveryKind;
  entityType?: GlobalAssistantEntityType;
};

const CURRENT_PAGE_FILTER_APPLY_PAGE_ID_PREFIX = '__current__:apply-filters:';

function getPageNameFromPath(pathname: string): string {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0) {
    return 'this page';
  }
  const [entity, page] = parts;
  const entityName = (() => {
    if (entity === 'company') return 'Company';
    if (entity === 'geography') return 'Geography';
    if (entity === 'role') return 'Role';
    return entity;
  })();
  if (page === 'screener') {
    return `${entityName} Discovery`;
  }
  if (page) {
    const pageName = page
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    return `${entityName} ${pageName}`;
  }
  return 'this page';
}

function getCurrentPageFilterApplyLabel(pathname: string): string {
  const pageName = getPageNameFromPath(pathname);
  return `Apply filters to ${pageName}`;
}

function looksLikeCustomDashPath(pathname: string): boolean {
  const segment = pathname.split('/').filter(Boolean)[0];
  return segment === 'company' || segment === 'geography' || segment === 'role';
}

function isDataDictionaryPath(pathname: string): boolean {
  const parts = pathname.split('/').filter(Boolean);
  return parts[0] === 'data-dictionary';
}

function isNormalDashboardFilterPath(pathname: string): boolean {
  const parts = pathname.split('/').filter(Boolean);
  const [entity, page] = parts;
  if (page === undefined || page === 'screener') {
    return false;
  }
  return entity === 'company' || entity === 'geography' || entity === 'role';
}

function isCurrentPageFilterApplyPath(pathname: string): boolean {
  return (
    isNormalDashboardFilterPath(pathname) || isScreenerV2DashboardPath(pathname)
  );
}

export function buildGlobalAssistantCurrentPageContext(args: {
  pathname: string;
  isInCustomDashEditMode: boolean;
  lastAppliedFilterContext?: LastAppliedFilterContext;
}): GlobalAssistantCurrentPageContext {
  const normalizedPrompt =
    args.lastAppliedFilterContext?.prompt.trim() || undefined;

  if (isDataDictionaryPath(args.pathname)) {
    return {
      pathname: args.pathname,
      kind: 'other',
    };
  }

  if (isTalentDiscoveryPublicPath(args.pathname)) {
    return {
      pathname: args.pathname,
      kind: 'discovery',
      discoveryKind: 'talent',
      ...(normalizedPrompt ? { baseDiscoveryPrompt: normalizedPrompt } : {}),
      ...(normalizedPrompt ? { baseFilterPrompt: normalizedPrompt } : {}),
    };
  }

  if (isScreenerV2DashboardPath(args.pathname)) {
    return {
      pathname: args.pathname,
      kind: 'discovery',
      discoveryKind: 'company',
      ...(normalizedPrompt ? { baseDiscoveryPrompt: normalizedPrompt } : {}),
      ...(normalizedPrompt ? { baseFilterPrompt: normalizedPrompt } : {}),
    };
  }

  if (args.isInCustomDashEditMode || looksLikeCustomDashPath(args.pathname)) {
    return {
      pathname: args.pathname,
      kind: 'custom_dash',
      ...(normalizedPrompt ? { baseFilterPrompt: normalizedPrompt } : {}),
    };
  }

  return {
    pathname: args.pathname,
    kind: 'other',
  };
}

export function getDiscoveryApplyPrompt(
  match: GlobalAssistantSuggestMatch,
  fallbackPrompt?: string
): string | undefined {
  const override = match.resolvedPromptForApply?.trim();
  if (override) {
    return override;
  }
  const fallback = fallbackPrompt?.trim();
  return fallback || undefined;
}

export function shouldAutoApplyDiscoveryMatch(
  match: GlobalAssistantSuggestMatch,
  pathname: string
): boolean {
  return Boolean(
    match.discoveryKind &&
      match.resolvedPromptForApply?.trim() &&
      globalAssistantPathsMatch(pathname, match.navigatePath)
  );
}

export function shouldAutoApplyDashboardFilterMatch(
  match: GlobalAssistantSuggestMatch,
  pathname: string
): boolean {
  return Boolean(
    !match.discoveryKind &&
      match.resolvedPromptForApply?.trim() &&
      isNormalDashboardFilterPath(match.navigatePath) &&
      globalAssistantPathsMatch(pathname, match.navigatePath)
  );
}

export function addCurrentPageFilterApplyOptionForDiscoveryMatch(args: {
  matches: GlobalAssistantSuggestMatch[];
  currentPathname: string;
  prompt?: string;
}): GlobalAssistantSuggestMatch[] {
  const { matches, currentPathname, prompt } = args;
  if (matches.length !== 1 || !isCurrentPageFilterApplyPath(currentPathname)) {
    return matches;
  }

  const discoveryMatch = matches[0];
  if (!discoveryMatch?.discoveryKind) {
    return matches;
  }
  if (globalAssistantPathsMatch(currentPathname, discoveryMatch.navigatePath)) {
    return matches;
  }
  if (
    !discoveryMatch.applyFiltersAfterNavigate &&
    !discoveryMatch.resolvedPromptForApply?.trim()
  ) {
    return matches;
  }

  const promptForApply = getDiscoveryApplyPrompt(discoveryMatch, prompt);
  if (!promptForApply) {
    return matches;
  }

  return [
    {
      pageId: `${CURRENT_PAGE_FILTER_APPLY_PAGE_ID_PREFIX}${currentPathname}`,
      name: getCurrentPageFilterApplyLabel(currentPathname),
      navigatePath: currentPathname,
      confidenceScore: discoveryMatch.confidenceScore,
      resolvedPromptForApply: promptForApply,
    },
    discoveryMatch,
  ];
}

export function hasCurrentPageFilterApplyOption(
  matches: GlobalAssistantSuggestMatch[]
): boolean {
  return matches.some((match) =>
    match.pageId.startsWith(CURRENT_PAGE_FILTER_APPLY_PAGE_ID_PREFIX)
  );
}
